import {
  Bar,
  Card,
  CardContent,
  CardHeader,
  ClockIcon,
  Divider,
  FactList,
  Theme,
  Typography,
  User,
  createStyles,
  makeStyles,
  useTheme,
} from '@forgeglobal/design-framework'
import { GenericOfferingFragment } from 'graphql/bindings/AdminBindings'
import React from 'react'

import { Routes } from 'util/Routes'
import {
  convertToCurrencyString,
  createAllocationBarCaption,
  createStatusChip,
  createTemporalNoteFieldText,
} from './CampaignHelpers'

const useStyles = makeStyles(({ spacing, palette }: Theme) =>
  createStyles({
    factList: {
      padding: spacing(2, 0, 4, 0),
    },
    divider: {
      borderLeft: `1px solid ${palette.divider}`,
      height: spacing(6),
      width: 1,
    },
    temporalNote: {
      display: 'flex',
      alignItems: 'center',
      margin: spacing(2, 0),
    },
    temporalNoteText: {
      marginLeft: spacing(2),
    },
    performanceBar: {
      padding: spacing(2, 0),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    statsContainer: {
      padding: spacing(2),
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
    },
    stats: {
      height: 50,
      background: palette.divider,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing(4),
    },
    content: {
      minHeight: spacing(30),
    },
  })
)

const CampaignCard: React.FC<{ offering: GenericOfferingFragment }> = ({ offering }) => {
  const {
    name,
    company,
    status,
    dollar_amount_at_starting_price_gross,
    dollar_amount_allocated_gross,
    id: campaignId,
    available_from,
    available_to,
    min_shares_to_close,
    starting_share_price,
    num_available_shares,
  } = offering
  const { logo_url } = company

  const classes = useStyles()
  const theme = useTheme()

  return (
    <Card>
      <CardHeader
        link={Routes.campaign(campaignId)}
        avatar={<User alt="React Logo" src={logo_url} />}
        title={name !== '' ? name : 'Unnamed Campaign'}
        subheader={`${convertToCurrencyString(dollar_amount_at_starting_price_gross)} at ${convertToCurrencyString(
          starting_share_price
        )} (Gross)`}
        secondaryIcon={createStatusChip(status)}
      />
      <CardContent className={classes.content} surface="main">
        <div className={classes.factList}>
          <FactList
            facts={[
              {
                header: `${convertToCurrencyString(dollar_amount_at_starting_price_gross)}`,
                subheader: 'total offering',
              },
              {
                header: `${convertToCurrencyString(dollar_amount_allocated_gross)}`,
                subheader: 'allocated',
              },
              {
                header: `${convertToCurrencyString(
                  dollar_amount_at_starting_price_gross - dollar_amount_allocated_gross
                )}`,
                subheader: 'remaining',
              },
            ]}
          />
        </div>
        <Divider />
        <div className={classes.temporalNote}>
          <ClockIcon />
          <div className={classes.temporalNoteText}>
            <Typography variant="caption" color="textSecondary" fontWeight="light">
              {createTemporalNoteFieldText(status, available_to, available_from)}
            </Typography>
          </div>
        </div>
        <Divider />
        <div className={classes.performanceBar}>
          <Bar
            filled={(dollar_amount_allocated_gross / dollar_amount_at_starting_price_gross) * 100}
            minimum={min_shares_to_close !== null ? (min_shares_to_close / num_available_shares) * 100 : undefined}
            color={status !== 'Live' ? theme.palette.text.secondary : theme.palette.secondary.main}
            caption={createAllocationBarCaption(
              min_shares_to_close * starting_share_price,
              dollar_amount_allocated_gross
            )}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export { CampaignCard }
